/**
 * ========================================
 * SUBSCRIPTION SERVICE
 * ========================================
 * API calls for the Subscription module (plans, lifecycle, quota usage) plus
 * the payment/subscription Admin Panel (plan & coupon CRUD, payment search/
 * refund/retry, revenue dashboard).
 * Endpoints: /api/v1/subscription/*, /api/v1/admin/payments/*
 */

import { apiClient } from './client';
import { handleAPIError } from './error-handler';
import { snakeToCamelDeep } from './case-transform';
import type {
  Coupon,
  CreateSubscriptionResponse,
  QuotaUsage,
  RevenueDashboard,
  SubscriptionPlan,
  UserSubscription,
} from '@/types/payment';

interface BackendEnvelope<T> {
  status: string;
  status_code?: number;
  message: string;
  data: T;
}

class SubscriptionService {
  private baseUrl = '/api/v1/subscription';
  private adminUrl = '/api/v1/admin/payments';

  // ── Customer-facing ──────────────────────────────────────

  async createSubscription(planId: string): Promise<CreateSubscriptionResponse> {
    try {
      const response = await apiClient.post<BackendEnvelope<CreateSubscriptionResponse>>(`${this.baseUrl}/create`, { plan_id: planId });
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async getCurrent(): Promise<UserSubscription | null> {
    try {
      const response = await apiClient.get<BackendEnvelope<UserSubscription | null>>(this.baseUrl);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async getUsage(): Promise<QuotaUsage> {
    try {
      const response = await apiClient.get<BackendEnvelope<QuotaUsage>>(`${this.baseUrl}/usage`);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async cancel(): Promise<UserSubscription> {
    try {
      const response = await apiClient.post<BackendEnvelope<UserSubscription>>(`${this.baseUrl}/cancel`, {});
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async resume(): Promise<UserSubscription> {
    try {
      const response = await apiClient.post<BackendEnvelope<UserSubscription>>(`${this.baseUrl}/resume`, {});
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async changePlan(newPlanId: string): Promise<UserSubscription> {
    try {
      const response = await apiClient.post<BackendEnvelope<UserSubscription>>(`${this.baseUrl}/change-plan`, { new_plan_id: newPlanId });
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  // ── Admin: Plans ──────────────────────────────────────────

  async adminListPlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<SubscriptionPlan[]>>(`${this.adminUrl}/plans`);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminCreatePlan(payload: Record<string, any>): Promise<SubscriptionPlan> {
    try {
      const response = await apiClient.post<BackendEnvelope<SubscriptionPlan>>(`${this.adminUrl}/plans`, payload);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminUpdatePlan(planId: string, payload: Record<string, any>): Promise<SubscriptionPlan> {
    try {
      const response = await apiClient.put<BackendEnvelope<SubscriptionPlan>>(`${this.adminUrl}/plans/${planId}`, payload);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminSetPlanActive(planId: string, active: boolean): Promise<SubscriptionPlan> {
    try {
      const response = await apiClient.post<BackendEnvelope<SubscriptionPlan>>(`${this.adminUrl}/plans/${planId}/${active ? 'enable' : 'disable'}`, {});
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  // ── Admin: Coupons ────────────────────────────────────────

  async adminListCoupons(): Promise<Coupon[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<Coupon[]>>(`${this.adminUrl}/coupons`);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminCreateCoupon(payload: Record<string, any>): Promise<Coupon> {
    try {
      const response = await apiClient.post<BackendEnvelope<Coupon>>(`${this.adminUrl}/coupons`, payload);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminDisableCoupon(couponId: string): Promise<Coupon> {
    try {
      const response = await apiClient.post<BackendEnvelope<Coupon>>(`${this.adminUrl}/coupons/${couponId}/disable`, {});
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  // ── Admin: Payments & Dashboard ───────────────────────────

  async adminSearchTransactions(query?: string, status?: string, page = 1, pageSize = 50) {
    try {
      const response = await apiClient.get<BackendEnvelope<{ items: any[]; total: number }>>(`${this.adminUrl}/transactions`, {
        params: { q: query, status, page, page_size: pageSize },
      });
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminRefund(transactionId: string, amount?: number) {
    try {
      const response = await apiClient.post<BackendEnvelope<any>>(`${this.adminUrl}/transactions/${transactionId}/refund`, { amount });
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminRetryPayment(transactionId: string) {
    try {
      const response = await apiClient.post<BackendEnvelope<any>>(`${this.adminUrl}/transactions/${transactionId}/retry`, {});
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminRevenueDashboard(): Promise<RevenueDashboard> {
    try {
      const response = await apiClient.get<BackendEnvelope<RevenueDashboard>>(`${this.adminUrl}/dashboard`);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;
